// Inicializacao condicional + LAZY do Sentry. Chamado UMA vez de
// `src/app/root.tsx` antes do `createRoot`. Se `VITE_SENTRY_DSN` ausente
// (default em dev local), o SDK do Sentry NAO e baixado — o `import()`
// dinamico nem dispara, removendo ~70 KB gzip do bundle inicial.
//
// Espelha mobile (`grana-money-app/src/shared/observability/sentry.ts`),
// adaptado para web:
//   - `browserTracingIntegration` em vez de `reactNativeTracingIntegration`.
//   - `replayIntegration` (web-exclusive) com mascaramento de inputs.
//   - Sample rates conservadores para nao explodir cota em prod.
//   - `beforeSend` redacta `Authorization` em headers se vazar.
//
// Phase 04 introduz; Phase 16 converte para lazy import (bundle perf);
// Phase 18 ajusta sample rates / release tracking conforme volume real.

import type * as SentryModuleType from '@sentry/react';

import { ENV } from '@/shared/config/env';

type SentryModule = typeof SentryModuleType;

let sentryModule: SentryModule | null = null;
let initialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Inicializa o Sentry ASSINCRONAMENTE com lazy import. No-op se DSN ausente.
 * Apos a primeira chamada, `initPromise` e cacheada — chamadas subsequentes
 * recuperam a mesma promise (idempotente).
 */
export function initSentry(): Promise<void> {
  if (initialized) return Promise.resolve();
  if (initPromise) return initPromise;
  if (!ENV.SENTRY_DSN) return Promise.resolve();

  initPromise = import('@sentry/react')
    .then((Sentry) => {
      sentryModule = Sentry;
      Sentry.init({
        dsn: ENV.SENTRY_DSN,
        environment: ENV.SENTRY_ENVIRONMENT,
        // `release` so vai se preenchido — `exactOptionalPropertyTypes` rejeita
        // `undefined` explicito quando a prop e opcional sem `| undefined`.
        ...(ENV.SENTRY_RELEASE ? { release: ENV.SENTRY_RELEASE } : {}),
        // Trace 100% em dev (debug), 10% em prod (cota + sinal).
        tracesSampleRate: ENV.IS_PROD ? 0.1 : 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllInputs: true,
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        // Replay so em sessao com erro — economiza cota e ainda preserva ctx.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event) {
          // Defensive: se algum interceptor interno do Sentry capturou Authorization
          // antes do logger redactar, removemos aqui. Nao confiamos em camadas
          // anteriores como unica linha de defesa.
          const headers = event.request?.headers as Record<string, string> | undefined;
          if (headers?.['Authorization']) {
            headers['Authorization'] = '[REDACTED]';
          }
          if (headers?.['authorization']) {
            headers['authorization'] = '[REDACTED]';
          }
          return event;
        },
      });
      initialized = true;
    })
    .catch((err: unknown) => {
      // Se o lazy import falhar (rede caindo, CDN bloqueando, etc), o app
      // segue funcionando sem observability — nao queremos travar o boot.
      console.warn('[sentry] falha ao carregar SDK lazy:', err);
    });

  return initPromise;
}

/**
 * Captura um erro inesperado no Sentry com contexto extra opcional.
 * No-op se Sentry nao foi inicializado (DSN ausente ou import pendente).
 */
export function captureError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized || !sentryModule) return;
  sentryModule.captureException(err, context ? { extra: context } : undefined);
}

/**
 * Adiciona um breadcrumb ao timeline do Sentry — util para HTTP, navegacao,
 * acoes do usuario. No-op se DSN ausente.
 *
 * Categorias canonicas: `http`, `navigation`, `auth`, `ui.click`, `console`.
 */
export interface BreadcrumbInput {
  category: string;
  message: string;
  data?: Record<string, unknown>;
  level?: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
}

export function addBreadcrumb(input: BreadcrumbInput): void {
  if (!initialized || !sentryModule) return;
  sentryModule.addBreadcrumb({
    category: input.category,
    message: input.message,
    ...(input.data !== undefined ? { data: input.data } : {}),
    level: input.level ?? 'info',
  });
}

/**
 * Marca o usuario corrente para correlacao em events. Chamada no setUser
 * do auth.store. Em logout, passar `null` limpa o scope.
 */
export function setSentryUser(user: { id: string; email: string } | null): void {
  if (!initialized || !sentryModule) return;
  if (user) {
    sentryModule.setUser({ id: user.id, email: user.email });
  } else {
    sentryModule.setUser(null);
  }
}

/**
 * Mostra dialog "Reportar bug" do Sentry — util no `<ErrorBoundary/>` para
 * capturar feedback do usuario quando uma tela crasha.
 */
export function showReportDialog(): void {
  if (!initialized || !sentryModule) return;
  sentryModule.showReportDialog();
}

// Test-only: forcar reset entre testes (Sentry e singleton global).
export const __testOnly__ = {
  reset(): void {
    initialized = false;
    initPromise = null;
    sentryModule = null;
  },
  isInitialized(): boolean {
    return initialized;
  },
};
